// File: src/components/web/PrivacyPolicy.jsx
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

const brand = {
  primary: "#B1336E",
  accentGold: "#E6B43C",
  accentGreen: "#67A43C",
};

const container = "mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8";
const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: "easeOut" },
};

const LAST_UPDATED = "10/07/2026";

const scrollTo = (id) => (e) => {
  e?.preventDefault?.();
  const targetId = (id || "").replace(/^#/, "");
  const el = document.getElementById(targetId);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    const base = window.location.hash.split("#")[1] || "";
    const basePath = base.includes("/") ? base.split("?")[0] : base;
    window.history.replaceState(null, "", `#${basePath}`);
  }
};

export default function PrivacyPolicy() {
  const toc = [
    ["Objeto y alcance", "objeto"],
    ["Identidad del responsable", "responsable"],
    ["Bases legales y finalidades", "bases-finalidades"],
    ["Datos que tratamos", "datos"],
    ["Fuentes de datos", "fuentes"],
    ["Mensajería y comunicaciones", "whatsapp"],
    ["Geolocalización y tracking", "geo"],
    ["Inventario, fulfillment y COD", "inventario-cod"],
    ["Analítica, automatización e IA", "analitica-ia"],
    ["Cookies y tecnologías similares", "cookies"],
    ["Conservación de datos", "plazos"],
    ["Encargados, destinatarios y transferencias", "encargados"],
    ["Transferencias internacionales", "transferencias"],
    ["Seguridad de la información", "seguridad"],
    ["Derechos ARCO y revocación", "arco"],
    ["Menores de edad", "menores"],
    ["Cambios a esta política", "cambios"],
    ["Contacto y canal de privacidad", "contacto"],
  ];

  return (
    <div className="bg-white text-slate-900">
      <header className="border-b border-slate-100 bg-white/90 backdrop-blur">
        <div className={`${container} flex h-16 items-center justify-between`}>
          <Link to="/" className="flex items-center gap-2" aria-label="FAST MAX">
            <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="FAST MAX" className="h-8 w-auto" />
            <span className="hidden sm:inline text-lg font-extrabold tracking-wide" style={{ color: brand.primary }}>
              FAST MAX
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-slate-50 sm:text-sm sm:px-4 sm:py-2"
          >
            Volver al inicio
            <ChevronRight className="size-4" />
          </Link>
        </div>
      </header>

      <main>
        <section className="bg-white">
          <div className={`${container} py-10 md:py-14`}>
            <motion.h1 {...fadeUp} className="text-3xl md:text-4xl font-extrabold" style={{ color: brand.primary }}>
              Política de Privacidad
            </motion.h1>
            <motion.p {...fadeUp} transition={{ delay: 0.05 }} className="mt-2 text-slate-600">
              Última actualización: {LAST_UPDATED}
            </motion.p>

            <motion.div
              {...fadeUp}
              transition={{ delay: 0.1 }}
              className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6"
            >
              <p>
                Esta Política describe cómo <strong>FAST MAX</strong> trata datos personales para prestar servicios de
                logística, entrega, contraentrega, inventario, fulfillment, atención al cliente final, seguimiento,
                conciliación, analítica e inteligencia operativa en Lima Metropolitana y Callao. Se rige por la <strong>Ley N.° 29733</strong>,
                su reglamento (D.S. 003-2013-JUS) y normas complementarias vigentes.
              </p>
            </motion.div>

            <motion.div {...fadeUp} transition={{ delay: 0.15 }} className="mt-8 grid gap-2 sm:grid-cols-2">
              {toc.map(([label, id]) => (
                <a
                  key={id}
                  href={`#${id}`}
                  onClick={scrollTo(id)}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold hover:bg-slate-50"
                >
                  {label}
                </a>
              ))}
            </motion.div>
            <Section id="objeto" title="1. Objeto y alcance">
              <p className="text-slate-700">
                Esta Política aplica a clientes, tiendas, destinatarios finales, usuarios de la plataforma, conductores,
                proveedores y otros participantes operativos cuando sus datos sean tratados para gestionar pedidos,
                inventario, entregas, contraentrega, soporte, comunicaciones, tracking, seguridad y mejora del servicio.
              </p>
            </Section>

            <Section id="responsable" title="2. Identidad del responsable">
              <ul className="pl-4 space-y-2 text-slate-700">
                <li><b>Responsable del tratamiento:</b> FAST MAX (la “Compañía”).</li>
                <li><b>Domicilio:</b> Lima, Perú.</li>
                <li><b>Correo de contacto:</b> <a className="underline" href="mailto:contacto@fastmax.pe">contacto@fastmax.pe</a></li>
                <li>
                  <b>Bancos de datos personales:</b> clientes y prospectos, logística y entregas, inventario y
                  fulfillment, atención y mensajería, conductores/operación, conciliación y pagos, según corresponda y
                  sujeto a registro o actualización ante la autoridad competente cuando sea aplicable.
                </li>
              </ul>
            </Section>

            <Section id="bases-finalidades" title="3. Bases legales y finalidades">
              <p className="text-slate-700">
                Tratamos datos bajo ejecución contractual o medidas precontractuales, consentimiento cuando corresponda,
                interés legítimo, cumplimiento de obligaciones legales, seguridad y defensa de derechos. Las finalidades
                principales son:
              </p>
              <ul className="pl-4 mt-2 space-y-2 text-slate-700">
                <li>Registrar tiendas, usuarios, puntos de recojo, destinatarios, direcciones, productos y entregas.</li>
                <li>Gestionar modalidades Clásico, Coordinado y Premium, incluyendo precio, reintentos, prioridad y elegibilidad por distrito.</li>
                <li>Planificar rutas, asignar conductores, estimar ventanas, mostrar tracking y registrar eventos operativos.</li>
                <li>Enviar comunicaciones operativas por WhatsApp, SMS, email, llamadas u otros canales habilitados.</li>
                <li>Gestionar contraentrega, medios de pago, manejo de efectivo, conciliación, depósitos y prevención de fraude.</li>
                <li>Gestionar inventario, almacén activo, fulfillment, packing, salidas, retornos e inactividad de productos.</li>
                <li>Atender consultas, reclamos, incidencias, reprogramaciones, devoluciones y soporte al cliente final.</li>
                <li>Medir desempeño, calidad operativa, tasa de éxito, rechazos, tiempos, zonas, presión logística y KPIs.</li>
                <li>Mejorar la plataforma, automatizar procesos, generar recomendaciones operativas y asistir decisiones con IA.</li>
                <li>Cumplir obligaciones legales, contables, tributarias, regulatorias y requerimientos de autoridad.</li>
              </ul>
            </Section>

            <Section id="datos" title="4. Datos que tratamos">
              <ul className="pl-4 space-y-2 text-slate-700">
                <li>Identificación y contacto: nombres, DNI/RUC cuando aplique, teléfono, WhatsApp, email, rol y usuario.</li>
                <li>Datos de tienda/cuenta: razón comercial, direcciones de recojo, preferencias, historial de pedidos y configuración.</li>
                <li>Datos de envío: destinatario, distrito, dirección, referencias, notas, horario, estado, prueba de entrega y eventos.</li>
                <li>Datos de modalidad y precio: servicio elegido, add-ons, reintentos, snapshots de precio, descuentos y fuente de registro.</li>
                <li>Datos de producto e inventario: SKU, cantidad, descripción, ubicación interna, movimientos, packing, salidas e inactividad.</li>
                <li>Datos transaccionales: monto COD, medio de pago, estado de cobro, conciliación, depósitos, ajustes y evidencias.</li>
                <li>Soporte y mensajería: contenido operativo necesario de chats, llamadas, incidencias y coordinación.</li>
                <li>Geodatos operativos: ubicación de conductor, trazas de ruta, ETA, hitos de entrega y validación de cobertura.</li>
                <li>Datos técnicos: IP, logs, dispositivo, navegador, sesión, errores, métricas de uso y seguridad.</li>
              </ul>
              <p className="text-slate-700 mt-2 text-sm">
                <b>Datos sensibles:</b> no solicitamos datos sensibles para la operación ordinaria. Si algún cliente o
                destinatario los envía sin requerimiento, FAST MAX podrá limitar, eliminar o no utilizar dicha información.
              </p>
            </Section>

            <Section id="fuentes" title="5. Fuentes de datos">
              <p className="text-slate-700">
                Recibimos datos del cliente o tienda mediante la plataforma, carga manual, Excel/CSV, inventario,
                integraciones, soporte o canales oficiales. También generamos datos por la operación logística, conductores,
                tracking, mensajería, cliente final, pagos, incidencias, pruebas de entrega y sistemas de seguridad.
              </p>
            </Section>

            <Section id="whatsapp" title="6. Mensajería y comunicaciones">
              <ul className="pl-4 space-y-2 text-slate-700">
                <li>Usamos WhatsApp, SMS, email, llamadas u otros canales para confirmar pedidos, coordinar entregas, informar tracking y gestionar incidencias.</li>
                <li>El cliente es responsable de la licitud de los datos de sus destinatarios y de informarles que FAST MAX podrá contactarlos por motivos operativos.</li>
                <li>Las comunicaciones operativas son necesarias para prestar el servicio. Las comunicaciones comerciales se gestionarán con consentimiento o base válida cuando corresponda.</li>
                <li>Los proveedores de mensajería pueden procesar datos fuera del Perú. Ver “Transferencias internacionales”.</li>
              </ul>
            </Section>
            <Section id="geo" title="7. Geolocalización y tracking">
              <p className="text-slate-700">
                Tratamos geolocalización de conductores, rutas, hitos, ventanas y estimaciones para asignar entregas,
                mostrar ubicación referencial, mejorar seguridad, auditar operación, calcular desempeño y ofrecer una
                experiencia de cliente final más clara. La ubicación puede ser aproximada y depende de conectividad,
                permisos del dispositivo y disponibilidad técnica.
              </p>
            </Section>

            <Section id="inventario-cod" title="8. Inventario, fulfillment y COD">
              <ul className="pl-4 space-y-2 text-slate-700">
                <li>Para inventario y fulfillment tratamos datos de productos, SKU, cantidades, movimientos, almacenamiento, packing, salidas, retornos e inactividad.</li>
                <li>Para COD tratamos importes a cobrar, método de pago, evidencia de cobro, conciliación, depósito, incidencias y prevención de fraude.</li>
                <li>Cuando intervienen proveedores de pagos, estos pueden actuar como responsables o encargados independientes según sus propias políticas. FAST MAX no necesita almacenar credenciales completas de tarjetas u otros datos financieros sensibles si el proveedor los procesa directamente.</li>
              </ul>
            </Section>

            <Section id="analitica-ia" title="9. Analítica, automatización e IA">
              <p className="text-slate-700">
                FAST MAX puede usar datos operativos, históricos y agregados para crear inteligencia logística: predicción
                de demoras, priorización, alertas, detección de anomalías, calidad de direcciones, desempeño por distrito,
                conductor, tienda, modalidad, rechazo, COD, inventario y rutas. También puede usar sistemas de IA para
                asistir a equipos internos con recomendaciones operativas, soporte, control de calidad y automatización.
              </p>
              <p className="mt-2 text-slate-700">
                Cuando sea razonable, usaremos datos agregados, anonimizados o seudonimizados. Las recomendaciones
                automatizadas apoyan la operación y no sustituyen, por sí solas, decisiones humanas cuando exista impacto
                relevante para el cliente o destinatario.
              </p>
            </Section>

            <Section id="cookies" title="10. Cookies y tecnologías similares">
              <p className="text-slate-700">
                Usamos cookies esenciales para sesión, seguridad y funcionamiento de la plataforma. También podemos usar
                tecnologías de medición o rendimiento para entender uso, errores y estabilidad. El usuario puede gestionar
                preferencias desde el navegador o mecanismos disponibles en la plataforma.
              </p>
            </Section>

            <Section id="plazos" title="11. Conservación de datos">
              <p className="text-slate-700">
                Conservamos datos durante la relación comercial y por el tiempo necesario para prestar el servicio,
                atender reclamos, cumplir obligaciones legales, contables o tributarias, resolver controversias, prevenir
                fraude y mantener trazabilidad operativa. Luego podremos eliminarlos, bloquearlos, agregarlos o
                anonimizarlos de forma segura.
              </p>
              <ul className="pl-4 mt-2 space-y-2 text-slate-700">
                <li>Los datos de entregas, COD, conciliación e inventario se conservan mientras sean necesarios para la operación y obligaciones aplicables.</li>
                <li>Los logs técnicos y de seguridad se conservan por el periodo necesario para auditoría, prevención de fraude y continuidad.</li>
                <li>Los datos de inventario inactivo pueden mantenerse mientras exista stock, deuda, incidencia, retorno pendiente o necesidad de trazabilidad.</li>
              </ul>
            </Section>

            <Section id="encargados" title="12. Encargados, destinatarios y transferencias">
              <p className="text-slate-700">
                Podemos compartir datos con proveedores que ayudan a operar FAST MAX: hosting/cloud, mensajería,
                mapas/geocodificación, pagos, conciliación, soporte, analítica, seguridad, almacenamiento, fulfillment,
                contabilidad, asesores, integraciones y autoridades cuando sea legalmente requerido. Estos proveedores
                deben tratar los datos conforme a finalidades limitadas, confidencialidad y medidas de seguridad.
              </p>
            </Section>

            <Section id="transferencias" title="13. Transferencias internacionales">
              <p className="text-slate-700">
                Algunos proveedores tecnológicos pueden procesar datos fuera del Perú. FAST MAX procurará adoptar medidas
                contractuales, técnicas y organizativas razonables para proteger los datos conforme a la normativa peruana
                y estándares de seguridad aplicables.
              </p>
            </Section>

            <Section id="seguridad" title="14. Seguridad de la información">
              <ul className="pl-4 space-y-2 text-slate-700">
                <li>Controles de acceso por rol, autenticación, segmentación de permisos y monitoreo de actividad.</li>
                <li>Cifrado en tránsito, registros de eventos, respaldos, auditoría y controles para continuidad operativa.</li>
                <li>Medidas internas para gestión de incidentes, confidencialidad del personal y revisión de proveedores.</li>
                <li>Ningún sistema es infalible; si detectas un incidente o uso no autorizado, debes contactarnos de inmediato.</li>
              </ul>
            </Section>
            <Section id="arco" title="15. Derechos ARCO y revocación">
              <p className="text-slate-700">
                Puedes ejercer tus derechos de Acceso, Rectificación, Cancelación y Oposición (ARCO), así como revocar el
                consentimiento cuando corresponda y solicitar información sobre el tratamiento de tus datos.
              </p>
              <ul className="pl-4 mt-2 space-y-2 text-slate-700">
                <li>
                  Envía tu solicitud a <a className="underline" href="mailto:contacto@fastmax.pe">contacto@fastmax.pe</a> indicando nombres, documento de identidad, petición concreta, datos de contacto y, si aplica, la cuenta o entrega relacionada.
                </li>
                <li>
                  Los plazos de atención se aplicarán conforme a la normativa peruana vigente y empezarán a contarse desde que recibamos una solicitud completa y verificable.
                </li>
                <li>
                  Si consideras que no hemos atendido adecuadamente tu solicitud, puedes acudir a la Autoridad Nacional de Protección de Datos Personales.
                </li>
              </ul>
            </Section>

            <Section id="menores" title="16. Menores de edad">
              <p className="text-slate-700">
                Nuestros servicios están dirigidos a comercios, empresas y destinatarios de entregas. No solicitamos datos
                de menores de edad para finalidades comerciales. Si detectas tratamiento indebido de datos de menores,
                escríbenos para revisarlo y tomar medidas razonables.
              </p>
            </Section>

            <Section id="cambios" title="17. Cambios a esta política">
              <p className="text-slate-700">
                Podemos actualizar esta Política por cambios legales, operativos, tecnológicos, de precios, modalidades,
                proveedores o funcionalidades. Publicaremos la versión vigente en el sitio o plataforma y, cuando
                corresponda, la comunicaremos por medios electrónicos.
              </p>
            </Section>

            <Section id="contacto" title="18. Contacto y canal de privacidad">
              <ul className="pl-4 space-y-2 text-slate-700">
                <li>Correo: <a className="underline" href="mailto:contacto@fastmax.pe">contacto@fastmax.pe</a></li>
                <li>Domicilio: Lima, Perú</li>
                <li>Asunto sugerido: “Solicitud ARCO / Privacidad”.</li>
              </ul>
              <p className="text-xs text-slate-500 mt-2">
                Esta política debe validarse legalmente para reflejar la razón social, RUC, domicilio fiscal, bancos de
                datos registrados, proveedores reales y plazos específicos aplicables a FAST MAX.
              </p>
            </Section>

            <div className="mt-10 flex justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm"
                style={{ background: brand.primary }}
              >
                Volver al inicio
                <ChevronRight className="size-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-28">
      <motion.h2 {...fadeUp} className="mt-10 text-xl font-extrabold" style={{ color: brand.primary }}>
        {title}
      </motion.h2>
      <motion.div {...fadeUp} transition={{ delay: 0.02 }} className="mt-3">
        {children}
      </motion.div>
    </section>
  );
}
